import { CollaborationStatus } from '@bp/bp-graphql-types';
import { Button, DeleteIcon, GridColumn, GridRow, Select, SelectOptionType } from '@bp/ui-components';
import { Field, FieldArrayRenderProps, useFormikContext } from 'formik';
import { useCreateSelectOptions } from 'hooks/useCreateSelectOptions';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { GroupFormType } from './GroupForm';

export type CollaborationGroupType = {
  uuid: string;
  collaborationStatus?: CollaborationStatus | null;
  name: string;
  groupAdmins: Array<{
    uuid: string;
    displayName?: string | null;
  }>;
  collaborationAdmins: Array<{
    uuid: string;
    displayName?: string | null;
  }>;
};

type GroupCollaborationFormRowProps = {
  index: number;
  helper: FieldArrayRenderProps;
  possibleCollaborations: CollaborationGroupType[];
  onDelete: (index: number) => void;
};

export const GroupCollaborationFormRow = ({
  index,
  helper,
  possibleCollaborations,
  onDelete,
}: GroupCollaborationFormRowProps) => {
  const { t } = useTranslation();

  const { values, setFieldValue, setFieldTouched, errors } = useFormikContext<GroupFormType>();

  const remainingCollaborations = possibleCollaborations.filter((pc) => !values.collaborationSchools.includes(pc.uuid));
  const thisCollaboration = possibleCollaborations.find((pc) => pc.uuid === values.collaborationSchools[index]);

  const collaborationOptions = useCreateSelectOptions(remainingCollaborations, 'uuid', 'name');
  const adminOptions = useCreateSelectOptions(thisCollaboration?.groupAdmins, 'uuid', 'displayName');

  return (
    <GridRow direction='column'>
      <GridRow spacingBottom='none'>
        <GridColumn width={11}>
          <Field
            as={Select}
            label={t('collaborations.collaboratingSchool', { count: 1 })}
            name={`${helper.name}.${index}.collaborationSchool`}
            options={collaborationOptions}
            value={{ value: thisCollaboration?.uuid, label: thisCollaboration?.name }}
            onChange={async (option: SingleValue<SelectOptionType>) => {
              let collaborationSchools: string[] = values.collaborationSchools;
              let schoolUuid: string;
              // replace or add school to collaboration schools
              if (thisCollaboration) {
                const newUuid = option?.value as string;
                const oldUuid = thisCollaboration?.uuid;
                collaborationSchools = values.collaborationSchools.map((uuid: string) =>
                  uuid === oldUuid ? newUuid : uuid,
                );
                schoolUuid = newUuid;
              } else {
                schoolUuid = option?.value as string;
                collaborationSchools.push(schoolUuid);
              }
              // set collaboration schools
              await setFieldTouched('collaborationSchools');
              await setFieldValue(
                'collaborationSchools',
                collaborationSchools.filter((c) => c !== 'new'),
              );
              // set admin for selected school
              const admins = possibleCollaborations.find((pc) => pc.uuid === schoolUuid)?.groupAdmins;
              await setFieldTouched('collaborationAdmins');
              await setFieldValue(
                `collaborationAdmins.${schoolUuid}`,
                admins?.map((a) => ({ uuid: a.uuid, displayName: a.displayName ?? '' })),
              );
            }}
            readonly={thisCollaboration?.groupAdmins.length === 0}
            menuPosition='fixed'
            isSearchable
          />
        </GridColumn>
        <GridColumn className='mt-4' width={1} align='end'>
          <Button icon={<DeleteIcon />} hierarchy='ghost' onClick={() => onDelete(index)} />
        </GridColumn>
      </GridRow>
      <GridRow spacingTop='none'>
        <GridColumn width={11} offset='right'>
          <Field
            as={Select}
            label={t('collaborations.schoolAdmins')}
            name={`${helper.name}.${index}.collaborationAdmins`}
            options={adminOptions}
            value={() => {
              const uuid = thisCollaboration?.uuid ?? '';
              return values.collaborationAdmins[uuid] && Array.isArray(values.collaborationAdmins[uuid])
                ? values.collaborationAdmins[uuid].map((a) => {
                    return { value: a.uuid, label: a.displayName };
                  })
                : [];
            }}
            onChange={async (options: MultiValue<SelectOptionType>) => {
              const selected =
                thisCollaboration?.groupAdmins?.filter((collaborationGroupAdmin) => {
                  return options.some((option) => {
                    return option.value === collaborationGroupAdmin.uuid;
                  });
                }) ?? [];
              await setFieldTouched('collaborationAdmins');
              await setFieldValue(
                `collaborationAdmins.${thisCollaboration?.uuid}`,
                selected.map((s) => {
                  return { uuid: s.uuid, displayName: s.displayName ?? '' };
                }),
              );
            }}
            disabled={!thisCollaboration}
            menuPosition='fixed'
            isSearchable
            isMulti
            error={errors.collaborationAdmins?.[thisCollaboration?.uuid ?? '']}
          />
        </GridColumn>
      </GridRow>
    </GridRow>
  );
};
